/**
 * FamilyTreeEngine — orchestrator class.
 * Owns state, delegates to treeLayout (pure) and treeRenderer (DOM).
 * Renders into a provided container element (not document.body).
 */
import {
  getEntries,
  getNeighbours,
  computeLayout,
  boundingBox,
  shift,
  isPerson,
  displayName,
} from './treeLayout'

import {
  makeDivs,
  placeDiv,
  setPeopleClasses,
  drawConnections,
} from './treeRenderer'

export default class FamilyTreeEngine {
  constructor(container, options = {}) {
    this.container = container
    this.lineHeight = options.lineHeight || 170
    this.paddingAmount = options.paddingAmount || 8
    this.photoDir = options.photoDir || 'photos/'
    this.defaultRootName = options.defaultRootName || null
    this.onInfoHover = options.onInfoHover || null
    this.onRootChange = options.onRootChange || null
    this.onTreeStats = options.onTreeStats || null

    this.rootName = null
    this.prevRootName = null
    this.downLimit = Infinity
    this.includeAll = false

    this.entries = null
    this.neighbours = null
    this.divs = null
  }

  load(text) {
    this.cleanup()
    try {
      this.entries = getEntries(text)
      this.neighbours = getNeighbours(this.entries)
      // Don't validate tree structure - allow multiple disconnected components
      // The user can select which component to view via the root selector
      // validateTreeStructure(this.neighbours)
    } catch (err) {
      console.error('Tree parse error:', err)
      throw err
    }

    // Pick initial root: use defaultRootName if valid, otherwise first person
    if (this.defaultRootName && this.neighbours[this.defaultRootName]) {
      this.rootName = this.defaultRootName
    } else {
      const people = Object.keys(this.neighbours).filter((n) => isPerson(n))
      this.rootName = people.length > 0 ? people[0] : null
    }

    if (!this.rootName) {
      console.warn('No people found in tree data')
      return
    }

    const onClickPerson = (name) => {
      this.setRoot(name)
    }

    this.divs = makeDivs(this.entries, this.neighbours, this.container, onClickPerson, this.photoDir)

    // Attach hover handlers for info pane
    if (this.onInfoHover) {
      for (const [, div] of Object.entries(this.divs)) {
        div.onmouseover = () => {
          if (div._treeInfo) {
            this.onInfoHover(div._treeInfo)
          }
        }
      }
    }

    this.drawTree()
  }

  setRoot(personName) {
    if (!this.neighbours || !this.neighbours[personName]) return
    this.rootName = personName
    if (this.onRootChange) this.onRootChange(displayName(personName))
    this.redraw()
  }

  setDetailLevel(value) {
    if (value === 'Everyone') {
      this.includeAll = true
      this.downLimit = Infinity
    } else {
      this.includeAll = false
      this.downLimit = Number(value)
    }
    this.redraw()
  }

  drawTree() {
    if (!this.divs || !this.rootName || !this.divs[this.rootName]) return

    setPeopleClasses(this.container, this.rootName, this.neighbours, this.divs)

    const layout = computeLayout(
      this.rootName,
      this.neighbours,
      this.divs,
      this.includeAll,
      this.downLimit,
      this.paddingAmount,
      this.lineHeight,
    )

    const box = boundingBox(layout, this.divs, this.paddingAmount)
    shift(layout, {
      x: 0,
      y: 0.5 * this.lineHeight - box.bottomLeft.y,
    })

    drawConnections(this.container, this.rootName, this.neighbours, this.divs, layout)

    for (const name of Object.keys(this.neighbours)) {
      if (isPerson(name)) {
        if (layout.hasOwnProperty(name)) {
          placeDiv(this.divs[name], layout[name].x, layout[name].y)
        } else {
          this.divs[name].style.visibility = 'hidden'
          this.divs[name].style.top = '100px'
          this.divs[name].style.left = '100px'
        }
      }
    }

    // Scroll to root only when it changes
    if (this.rootName !== this.prevRootName) {
      this.scrollToRoot()
      this.prevRootName = this.rootName
    }

    // Notify root change
    if (this.onRootChange) this.onRootChange(displayName(this.rootName))

    // Compute stats
    this.computeStats(layout)
  }

  redraw() {
    // Remove drawn lines
    const lines = this.container.querySelectorAll('.drawn-line')
    lines.forEach((line) => line.parentNode.removeChild(line))

    // Batch remove position classes with 2 queries instead of 5
    const posEls = this.container.querySelectorAll('[class*="pos-"]')
    posEls.forEach((el) => {
      el.classList.remove('pos-root', 'pos-ancestor', 'pos-blood', 'pos-descendant', 'pos-other')
    })

    this.drawTree()
  }

  scrollToRoot() {
    if (!this.divs || !this.rootName || !this.divs[this.rootName]) return
    const element = this.divs[this.rootName]
    const containerRect = this.container.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()

    const scrollLeft =
      elementRect.left - containerRect.left + this.container.scrollLeft - this.container.clientWidth / 2 + element.offsetWidth / 2
    const scrollTop =
      elementRect.top - containerRect.top + this.container.scrollTop - this.container.clientHeight / 2 + element.offsetHeight / 2

    this.container.scrollTo({
      left: Math.max(0, scrollLeft),
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    })
  }

  computeStats(layout) {
    if (!this.onTreeStats) return
    let ancestors = 0, descendants = 0, blood = 0, others = 0
    for (const [person, div] of Object.entries(this.divs)) {
      if (!layout.hasOwnProperty(person)) continue
      if (div.classList.contains('pos-ancestor')) ancestors++
      if (div.classList.contains('pos-descendant')) descendants++
      if (div.classList.contains('pos-blood')) blood++
      if (div.classList.contains('pos-other')) others++
    }
    this.onTreeStats({ ancestors, descendants, blood, others, total: ancestors + descendants + blood + others + 1 })
  }

  cleanup() {
    if (!this.container) return
    this.container.replaceChildren()
    this.entries = null
    this.neighbours = null
    this.divs = null
    this.rootName = null
    this.prevRootName = null
  }

  destroy() {
    this.cleanup()
  }

  getPeople() {
    if (!this.neighbours) return []
    return Object.keys(this.neighbours).filter((n) => isPerson(n)).map(displayName)
  }
}
